import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-tailwind-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { ChromiumDialog } from '@chancenwerk/client/design/components/cr-dialog.js';
import { ChromiumSnackbar } from '@chancenwerk/client/design/components/cr-snackbar.js';
import { Onboarding } from '@chancenwerk/shared/master-data-management/users/entities/Mitarbeiter.js';
import { mutationWorkshopMigrationOnboarding } from '@chancenwerk/shared/workshops/api/mutationWorkshopMigrationOnboarding.js';
import type { Workshop } from '@chancenwerk/shared/workshops/entities/Workshop.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

/**
 * @customElement akademie-component-workshop-migration-onboarding
 */
@customElement('akademie-component-workshop-migration-onboarding')
export class AkdamieComponentCardTitle extends ChemistryLitElement {
  @property({ attribute: false }) workshops: Workshop[] = [];

  @state() private readonly selectedWorkshop = new RXController<Maybe<Workshop>>(this, null);
  @state() private readonly selectedOldType = new RXController<Maybe<string>>(this, null);

  override render() {
    return html` <cr-card space="md" padding="md">
      <chancen-component-card-title> Migration von alten Einarbeitungs-Daten </chancen-component-card-title>
      <cr-text>
        Beim Migrieren von alten Daten werden die Daten der alten statischen Daten in einen neuen Workshop deiner Wahl
        migriert.
      </cr-text>
      <cr-flex horizontal crossaxis-center space="md">
        <cr-dropdown-selection
          ${css({ width: '400px' })}
          placeholder="altes Einarbeitungs-Feld"
          .value=${this.selectedOldType.value}
          .selectables=${Object.keys(Onboarding.onboardinTypes)}
          .renderString=${(t: string) => Onboarding.onboardinTypes[t]}
          @value-picked=${(e: ValueEvent<Maybe<string>>) => this.selectedOldType.next(e.detail.value)}
          clearable
        >
        </cr-dropdown-selection>
        <cr-dropdown-selection
          ${css({ width: '400px' })}
          placeholder="neuer Workshop"
          .value=${this.selectedWorkshop.value}
          .selectables=${this.workshops}
          .renderString=${(w: Workshop) => w.title}
          @value-picked=${(e: ValueEvent<Maybe<Workshop>>) => this.selectedWorkshop.next(e.detail.value)}
          clearable
        >
        </cr-dropdown-selection>
      </cr-flex>
      <cr-flex ${css({ maxWidth: '300px' })}> ${this._button()} </cr-flex>
    </cr-card>`;
  }

  private _button() {
    if (!this.selectedOldType.value || !this.selectedWorkshop.value) {
      return html`<cr-button disabled>Starten</cr-button>`;
    }

    return html`<cr-button @click=${() => this._startMigration()}>Starten</cr-button>`;
  }

  private async _startMigration() {
    await ChromiumDialog.guard(
      async () => {
        try {
          if (!this.selectedOldType.value || !this.selectedWorkshop.value) {
            throw new Error('Bitte wähle eine Workshop-Kombination aus');
          }

          await mutationWorkshopMigrationOnboarding(this.selectedWorkshop.value._id, this.selectedOldType.value)();
          this.selectedOldType.next(null);
          this.selectedWorkshop.next(null);
          this.requestUpdate();
        } catch (error: any) {
          await ChromiumSnackbar.show(error.message as string);
        }
      },
      {
        hasToConfirm: true,
        confirmText: 'Bist du dir sicher?',
      },
    );
  }
}
